import React, { useEffect, useMemo, useState } from 'react';
import { DeleteIconBox, EditIconBox, FlexWrapper, LinkStyled } from '../../theme/common_style';
import { DeleteIcon, EditIcon, TrashIconNew } from '../../theme/SvgIcons';
import { useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Drawer, Segmented } from 'antd';
import '../../theme/antCustomComponents.css';
import styled from '@emotion/styled/macro';
import GeneralInfo from './generalInfo/GeneralInfo';
import ChangeRequest from './changeRequest/ChangeRequest';
import Milestone from './milestone/Milestone';
import { useDispatch, useSelector } from 'react-redux';
import ProjectActivity from './ProjectActivity';
import ProjectDocuments from './document/Documents';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import { deleteProjectApi } from '../../redux/project/apiRoute';
import { toast } from 'react-toastify';
import AddProject from './AddProject';
import { checkPermission } from '../../utils/common_functions';
import ProjectReporting from './reporting/ProjectReporting';
import { updateActiveTabPro, updateIsManager } from '../../redux/project/ProjectSlice';
import { projectEmpRole } from '../../utils/constant';
import Notes from './notes/Notes';
import Feedback from './feedback/Feedback';
import { updateActivityDrawer } from '../../redux/sidebar/SidebarSlice';

const ProjectDetails = () => {
  const dispatch = useDispatch();
  const selectedTab = useSelector((state) => state?.projectSlice?.activeTabPro);
  const activityDrawer = useSelector((state) => state?.sidebar?.isActivityDrawer);
  const location = useLocation();
  const { name, id } = location.state || {};
  const { isEmployee } = useSelector((e) => e.userInfo);
  const [editData, setEditData] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const [editModal, setEditModal] = useState(false);
  const [updated, setUpdated] = useState(false);
  const { permissions, user_details } = useSelector((state) => state?.userInfo?.data);
  let permissionSection = 'Projects';
  const canDelete = checkPermission(permissionSection, 'del', permissions);
  const canUpdate = checkPermission(permissionSection, 'update', permissions);
  const [loading, setLoading] = useState(false);
  const { isManager } = useSelector((state) => state?.projectSlice);

  const haveAccess = useMemo(() => {
    if (!isEmployee || isManager) {
      return true;
    }
    return false;
  }, [isEmployee, isManager]);

  const handleProjectList = () => {
    setUpdated(!updated);
  };

  const handleDelete = async () => {
    if (id) {
      try {
        setDeleteLoading(true);
        let res = await deleteProjectApi(id);
        if (res?.statusCode === 200) {
          navigate('/project');
          setDeleteModal(false);
        } else {
          toast.error(res?.message || 'Something went wrong');
        }
      } catch (err) {
        toast.error(err?.message || 'Something went wrong');
      } finally {
        setDeleteLoading(false);
      }
    } else {
      toast.error('Project not found');
    }
  };

  useEffect(() => {
    if (!id) {
      navigate('/project', { replace: true });
    } else if (editData) {
      const res = editData?.project_Assignee?.find(
        (item) =>
          item?.emp_id?.id === user_details?.id &&
          (item?.role === projectEmpRole.MANAGER || item?.role === projectEmpRole.INCHARGE)
      );
      dispatch(updateIsManager(res ? true : false));
    }
  }, [editData]);

  const breadcrumbArr = [
    {
      title: <LinkStyled to="/project">Project</LinkStyled>
    },
    {
      title: name
    }
  ];

  return (
    <div>
      {editModal && (
        <AddProject
          open={editModal}
          close={() => setEditModal(false)}
          handleProjectList={handleProjectList}
          editDetails={editData}
        />
      )}
      {deleteModal && (
        <ConfirmationModal
          open={deleteModal}
          onCancel={() => setDeleteModal(false)}
          title={'Delete Project'}
          onSubmit={handleDelete}
          buttonName={'Delete'}
          description={'Are you sure you want to delete this project?'}
          iconBG={'#FB4A49'}
          icon={<TrashIconNew />}
          loading={deleteLoading}
        />
      )}
      {activityDrawer && (
        <Drawer
          width={490}
          title="Activity"
          placement="right"
          closable={true}
          prefixCls="activityCustomDrawer"
          onClose={() => dispatch(updateActivityDrawer(false))}
          open={activityDrawer}
          key="right">
          <ProjectActivity projectId={id} />
        </Drawer>
      )}
      <FlexWrapper justify="space-between" margin="0 0 13px">
        <Breadcrumb items={breadcrumbArr} />
      </FlexWrapper>
      <FlexWrapper justify="space-between">
        <Segmented
          disabled={loading}
          prefixCls="antCustomSegmented"
          options={[
            'General Info',
            'Documents',
            'Change Request',
            'Milestone',
            'Reporting',
            'Notes',
            ...(haveAccess ? ['Feedbacks'] : [])
          ]}
          value={selectedTab}
          onChange={(value) => {
            dispatch(updateActiveTabPro(value));
          }}
          className="responsive-segmented"
        />
        {!isEmployee && selectedTab === 'General Info' && (
          <FlexWrapperIcon gap="6px">
            {canUpdate && (
              <EditIconBox canUpdate={canUpdate} onClick={() => setEditModal(true)}>
                <EditIcon />
              </EditIconBox>
            )}
            {canDelete && (
              <DeleteIconBox canDelete={canDelete} onClick={() => setDeleteModal(true)}>
                <DeleteIcon />
              </DeleteIconBox>
            )}
          </FlexWrapperIcon>
        )}
      </FlexWrapper>
      {id && (
        <ContentWrapper>
          {selectedTab === 'General Info' && (
            <GeneralInfo
              setEditData={setEditData}
              updated={updated}
              editData={editData}
              loading={loading}
              setLoading={setLoading}
            />
          )}
          {selectedTab === 'Documents' && <ProjectDocuments />}
          {selectedTab === 'Change Request' && <ChangeRequest />}
          {selectedTab === 'Milestone' && <Milestone />}
          {selectedTab === 'Reporting' && <ProjectReporting />}
          {selectedTab === 'Notes' && <Notes />}
          {haveAccess && selectedTab === 'Feedbacks' && <Feedback />}
        </ContentWrapper>
      )}
    </div>
  );
};

export default ProjectDetails;

const ContentWrapper = styled.div`
  margin: 20px 0 0 !important;
`;
const FlexWrapperIcon = styled.div`
  display: flex;
  gap: 10px;
  @media (max-width: 868px) {
    width: 100%;
    justify-content: end;
    align-items: end;
    margin-top: 10px;
  }
`;
